/**
 * RoomFurniturePanel — full furniture CRUD inside the room-detail drawer.
 *
 * Tabs:
 *   • List    — existing items in this room (+ quick delete)
 *   • Catalog — pick from the building's master furniture catalog
 *   • Manual  — free-form name + quantity + condition
 *   • Template — apply a multi-item template to this room in one click
 *
 * Endpoints:
 *   GET    /api/properties/rooms/{id}/furniture?include_tickets=true
 *   POST   /api/properties/rooms/{id}/furniture
 *   DELETE /api/furniture/{id}
 *   GET    /api/furniture-catalog?building_id={bid}
 *   GET    /api/furniture-templates
 *   POST   /api/furniture-templates/apply
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Icons, EmptyState, Button } from '../../shared/ui';
import api from '../../utils/api';

const CONDITION_KEYS = ['excellent', 'good', 'fair', 'poor', 'broken'];

// Helper — all furniture-panel keys live under `enhanced.furniturePanel.*`.
const fk = (k) => `properties:enhanced.furniturePanel.${k}`;

export default function RoomFurniturePanel({ roomId, buildingId }) {
  const { t } = useTranslation(['properties', 'common']);
  const qc = useQueryClient();
  const [mode, setMode] = useState('list');  // 'list' | 'catalog' | 'manual' | 'template'

  const { data: furniture = [], isLoading } = useQuery({
    queryKey: ['room-furniture', roomId],
    queryFn: () =>
      api.get(`/api/properties/rooms/${roomId}/furniture?include_tickets=true`).then(r => {
        const d = r.data; return Array.isArray(d) ? d : (d?.furniture ?? []);
      }),
    enabled: !!roomId,
    staleTime: 60 * 1000,
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/api/furniture/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['room-furniture', roomId] });
      toast.success(t(fk('toast.deleted')));
    },
    onError: () => toast.error(t(fk('toast.deleteFailed'))),
  });

  // Catalog management lives at the building/app level (PropertyManagement page).
  // The room drawer only consumes existing catalog items via Manual or Template.
  const TABS = ['list', 'manual', 'template'];

  return (
    <div className="space-y-3">
      {/* Mode tabs */}
      <div className="inline-flex rounded-md border border-stone-200 bg-stone-50 p-0.5 text-xs flex-wrap">
        {TABS.map(m => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-2.5 py-1 rounded ${mode === m ? 'bg-white shadow-sm text-stone-900 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
          >
            {t(fk(`mode.${m}`))}
          </button>
        ))}
      </div>

      {mode === 'list' && (
        <FurnitureList
          furniture={furniture}
          isLoading={isLoading}
          onDelete={(id) => {
            if (window.confirm(t(fk('confirmDelete')))) {
              remove.mutate(id);
            }
          }}
        />
      )}

      {mode === 'catalog' && buildingId && (
        <CatalogAddForm roomId={roomId} buildingId={buildingId} onSuccess={() => setMode('list')} />
      )}

      {mode === 'manual' && (
        <ManualAddForm roomId={roomId} onSuccess={() => setMode('list')} />
      )}

      {mode === 'template' && (
        <TemplateApplyForm roomId={roomId} onSuccess={() => setMode('list')} />
      )}
    </div>
  );
}

function FurnitureList({ furniture, isLoading, onDelete }) {
  const { t } = useTranslation('properties');

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-10 bg-stone-100 rounded animate-pulse" />)}
      </div>
    );
  }
  if (!furniture.length) {
    return <EmptyState compact icon={<Icons.Cube />} title={t('enhanced.detail.emptyFurniture')} />;
  }
  return (
    <div className="border border-stone-200 rounded-lg divide-y divide-stone-100">
      {furniture.map((item) => (
        <div key={item.id} className="flex items-center gap-2 px-3 py-2">
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-stone-900 truncate">{item.name || item.furniture_name || '—'}</p>
            <div className="flex items-center gap-2 text-[11px] text-stone-500 mt-0.5">
              <span>×{item.quantity || 1}</span>
              {item.condition && <span>· {t(`enhanced.roomStatus.${item.condition}`, { defaultValue: item.condition })}</span>}
              {item.tickets_count > 0 && (
                <span className="text-amber-600">· {t(fk('openTickets').replace('properties:', ''), { count: item.tickets_count })}</span>
              )}
            </div>
          </div>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1 text-stone-300 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors"
            title={t(fk('delete').replace('properties:', ''))}
          >
            <Icons.Trash size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function CatalogAddForm({ roomId, buildingId, onSuccess }) {
  const { t } = useTranslation(['properties', 'common']);
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [condition, setCondition] = useState('good');

  const { data: catalog = [], isLoading } = useQuery({
    queryKey: ['furniture-catalog', buildingId],
    queryFn: () => api.get(`/api/furniture-catalog?building_id=${buildingId}`).then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.items ?? []);
    }),
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });

  const add = useMutation({
    mutationFn: () => {
      const item = catalog.find(c => String(c.id) === selectedId);
      return api.post(`/api/properties/rooms/${roomId}/furniture`, {
        catalog_id: item?.id,
        name: item?.name,
        quantity,
        condition,
      }).then(r => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['room-furniture', roomId] });
      toast.success(t(fk('toast.added')));
      setSelectedId(''); setQuantity(1); setCondition('good');
      onSuccess?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t(fk('toast.addFailed'))),
  });

  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1';

  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>{t(fk('label.catalog'))}</label>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          disabled={isLoading}
          className={fieldCls}
        >
          <option value="">{t(fk('label.pickCatalogItem'))}</option>
          {catalog.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {!isLoading && catalog.length === 0 && (
          <p className="text-[11px] text-stone-400 mt-1">{t(fk('label.emptyCatalog'))}</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>{t(fk('label.quantity'))}</label>
          <input
            type="number" min={1}
            value={quantity}
            onChange={e => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t(fk('label.condition'))}</label>
          <select
            value={condition}
            onChange={e => setCondition(e.target.value)}
            className={fieldCls}
          >
            {CONDITION_KEYS.map(c => (
              <option key={c} value={c}>{t(`properties:condition.${c}`, { defaultValue: c })}</option>
            ))}
          </select>
        </div>
      </div>
      <Button
        kind="primary"
        size="sm"
        className="w-full justify-center"
        loading={add.isPending}
        disabled={!selectedId || add.isPending}
        onClick={() => add.mutate()}
      >
        {t(fk('action.addFromCatalog'))}
      </Button>
    </div>
  );
}

function ManualAddForm({ roomId, onSuccess }) {
  const { t } = useTranslation(['properties', 'common']);
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    quantity: 1,
    condition: 'good',
    description: '',
  });

  const add = useMutation({
    mutationFn: () => api.post(`/api/properties/rooms/${roomId}/furniture`, form).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['room-furniture', roomId] });
      toast.success(t(fk('toast.added')));
      setForm({ name: '', quantity: 1, condition: 'good', description: '' });
      onSuccess?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t(fk('toast.addFailed'))),
  });

  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1';

  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>{t(fk('label.name'))} *</label>
        <input
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder={t(fk('label.namePlaceholder'))}
          className={fieldCls}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>{t(fk('label.quantity'))}</label>
          <input
            type="number" min={1}
            value={form.quantity}
            onChange={e => setForm({ ...form, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t(fk('label.condition'))}</label>
          <select
            value={form.condition}
            onChange={e => setForm({ ...form, condition: e.target.value })}
            className={fieldCls}
          >
            {CONDITION_KEYS.map(c => (
              <option key={c} value={c}>{t(`properties:condition.${c}`, { defaultValue: c })}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>{t(fk('label.notes'))}</label>
        <textarea
          rows={2}
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          className={`${fieldCls} h-auto py-2 resize-y`}
        />
      </div>
      <Button
        kind="primary"
        size="sm"
        className="w-full justify-center"
        loading={add.isPending}
        disabled={!form.name.trim() || add.isPending}
        onClick={() => add.mutate()}
      >
        {t(fk('action.add'))}
      </Button>
    </div>
  );
}

function TemplateApplyForm({ roomId, onSuccess }) {
  const { t } = useTranslation(['properties', 'common']);
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState('');

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['furniture-templates'],
    queryFn: () => api.get('/api/furniture-templates').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.templates ?? []);
    }),
    staleTime: 5 * 60 * 1000,
  });

  const apply = useMutation({
    mutationFn: () =>
      api.post('/api/furniture-templates/apply', {
        template_id: parseInt(selectedId, 10),
        room_ids: [parseInt(roomId, 10)],
      }).then(r => r.data),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['room-furniture', roomId] });
      const count = result?.created_furniture_count ?? result?.created_count ?? 0;
      toast.success(t(fk('toast.templateApplied'), { count }));
      onSuccess?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t(fk('toast.templateApplyFailed'))),
  });

  const selected = templates.find(tmpl => String(tmpl.id) === selectedId);

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
          {t(fk('label.template'))}
        </label>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          disabled={isLoading}
          className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
        >
          <option value="">{t(fk('label.pickTemplate'))}</option>
          {templates.map(tmpl => (
            <option key={tmpl.id} value={tmpl.id}>
              {tmpl.name} ({(tmpl.items?.length || 0) + (tmpl.catalog_items?.length || 0)} {t(fk('itemsShort'))})
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-2 text-[11px] space-y-0.5 max-h-32 overflow-y-auto">
          {[...(selected.items || []), ...(selected.catalog_items || [])].map((it, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-stone-700">{it.name || it.item_name}</span>
              <span className="text-stone-500">×{it.quantity || 1}</span>
            </div>
          ))}
        </div>
      )}

      <Button
        kind="primary"
        size="sm"
        className="w-full justify-center"
        loading={apply.isPending}
        disabled={!selectedId || apply.isPending}
        onClick={() => apply.mutate()}
      >
        {t(fk('action.applyTemplate'))}
      </Button>
    </div>
  );
}
